import type {
	FC,
	MouseEvent as ReactMouseEvent,
	ReactNode,
	SVGProps,
} from "react"
import { useRef } from "react"
import { Link } from "@tanstack/react-router"
import { IconChevronRightOutline18 as ChevronRight } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"

interface Props {
	Icon?: LucideIcon
	title: ReactNode
	description?: ReactNode
	action?: ReactNode
	className?: string
	link?: string
	// A row that opens something without a route of its own, like a picker first.
	onClick?: () => void
	disabled?: boolean
}

// Controls that a click activates, mirroring what a native <label for> would do.
const TOGGLE_SELECTOR = [
	'[role="switch"]',
	'[role="checkbox"]',
	'[role="radio"]',
	'input[type="checkbox"]',
	'input[type="radio"]',
].join(",")

const POPUP_SELECTOR = [
	'[role="combobox"]',
	"select",
	'[aria-haspopup="menu"]',
	'[aria-haspopup="listbox"]',
	'[aria-haspopup="dialog"]',
].join(",")

const TEXT_FIELD_SELECTOR = [
	'input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="file"])',
	"textarea",
].join(",")

const INTERACTIVE_SELECTOR = [
	"a",
	"button",
	"input",
	"textarea",
	"select",
	'[role="switch"]',
	'[role="checkbox"]',
	'[role="radio"]',
	'[role="combobox"]',
	'[contenteditable="true"]',
].join(",")

export const SettingItem = ({
	Icon,
	title,
	description,
	action,
	className,
	link,
	onClick,
	disabled,
}: Props) => {
	const opens = !!link || !!onClick
	const actionRef = useRef<HTMLDivElement>(null)

	const activateControl = (event: ReactMouseEvent<HTMLDivElement>) => {
		if (opens) return
		const target = event.target
		// A click in a portaled menu still bubbles here through React; re-pressing the trigger would close it.
		if (!(target instanceof Node) || !event.currentTarget.contains(target))
			return
		if (target instanceof Element && target.closest(INTERACTIVE_SELECTOR))
			return
		const container = actionRef.current
		if (!container) return

		const toggle = container.querySelector<HTMLElement>(TOGGLE_SELECTOR)
		if (toggle) {
			toggle.click()
			return
		}

		// Radix menu and select triggers open on pointerdown, so a click alone is a no-op.
		const popup = container.querySelector<HTMLElement>(POPUP_SELECTOR)
		if (popup) {
			openPopup(popup)
			return
		}

		container.querySelector<HTMLElement>(TEXT_FIELD_SELECTOR)?.focus()
	}

	const content = (
		<div
			role="presentation"
			onClick={opens ? undefined : activateControl}
			className={cn(
				"flex select-none items-center justify-between gap-4",
				!opens && className,
			)}
		>
			<div className="flex flex-row items-center gap-3">
				{Icon && (
					<span className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-gray-100 text-primary dark:bg-gray-800 [&_svg]:h-[18px] [&_svg]:w-[18px]">
						<Icon />
					</span>
				)}
				<div className="flex flex-col gap-0.5">
					<div className="text-sm font-medium">{title}</div>
					{description && (
						<div className="text-xs font-normal tracking-tight text-secondary">
							{description}
						</div>
					)}
				</div>
			</div>
			<div ref={actionRef} className="contents">
				{action}
			</div>
			{opens && (
				<ChevronRight className="h-3.5 w-3.5 flex-none transform-gpu text-tertiary" />
			)}
		</div>
	)

	if (link) {
		return (
			<Link to={link} className={cn(OPENS_ROW_CLASS, className)}>
				{content}
			</Link>
		)
	}
	if (onClick) {
		return (
			<button
				type="button"
				onClick={onClick}
				disabled={disabled}
				className={cn(
					OPENS_ROW_CLASS,
					"w-[calc(100%+2rem)] text-left disabled:pointer-events-none disabled:opacity-50",
					className,
				)}
			>
				{content}
			</button>
		)
	}
	return content
}

function openPopup(element: HTMLElement) {
	// A popover trigger, like the searchable select's, opens on click.
	if (
		element.tagName === "SELECT" ||
		element.getAttribute("aria-haspopup") === "dialog"
	) {
		element.click()
		return
	}
	const init = { bubbles: true, cancelable: true, button: 0 }
	element.dispatchEvent(
		typeof PointerEvent === "undefined"
			? new MouseEvent("pointerdown", init)
			: new PointerEvent("pointerdown", { ...init, pointerType: "mouse" }),
	)
}

type LucideIcon = FC<SVGProps<SVGSVGElement>>

// A row that opens something matches SettingsNavRow: 16px padding and a hover that fills
// the card edge to edge, by bleeding through the card's 16px padding.
const OPENS_ROW_CLASS =
	"settings-opens-row block -mx-4 px-4 sm:py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 first:rounded-t-[10px] last:rounded-b-[10px] sm:first:-mt-4 sm:last:-mb-4 max-sm:first:rounded-t-2xl max-sm:last:rounded-b-2xl"
